import { useState } from 'react';
import {
  Smartphone,
  Share,
  Plus,
  Download,
  CheckCircle2,
  Chrome,
  MoreVertical,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import usePwaInstall from '@/hooks/usePwaInstall';
import { toast } from 'sonner';

/*
 * InstallPwaProfileCard
 *
 * Always-available install entry point in the Profile page. The card
 * remains visible while the app is not yet installed; the action button
 * adapts to whatever the current browser supports:
 *
 *   - Native prompt captured (Chromium with engagement) → "Install app"
 *     fires the captured beforeinstallprompt directly.
 *   - iOS Safari → inline "Share → Add to Home Screen" instructions.
 *   - Chromium without captured prompt yet → instructions for the menu
 *     install flow (Address bar install icon / three-dot menu).
 *   - Anything else (Firefox / Safari desktop / etc.) → friendly note
 *     suggesting Chrome/Edge or iOS Safari for a one-tap install.
 *
 * If the app is already installed, the card flips to a quiet confirmation.
 */
export default function InstallPwaProfileCard() {
  const {
    installed,
    canShowProfileInstall,
    hasNativePrompt,
    isIos,
    isAndroidChromium,
    isDesktopChromium,
    isFirefoxMobile,
    isSamsungBrowser,
    isOperaMobile,
    isMobile,
    promptInstall,
  } = usePwaInstall();
  const [expanded, setExpanded] = useState(false);

  if (installed) {
    return (
      <div
        className="rounded-xl border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/10 p-4 flex items-center gap-3"
        data-testid="pwa-profile-installed"
      >
        <CheckCircle2 className="w-5 h-5 text-[hsl(var(--primary))]" />
        <div>
          <p className="text-sm font-medium">GroovLens is installed</p>
          <p className="text-xs text-muted-foreground">
            You&apos;re running the installed app. Enjoy the faster launches.
          </p>
        </div>
      </div>
    );
  }

  if (!canShowProfileInstall) return null;

  // Decide which instruction track this device falls into.
  // We use mutually exclusive flags so the rendered copy stays consistent.
  const track = (() => {
    if (hasNativePrompt) return 'native';
    if (isIos) return 'ios';
    if (isFirefoxMobile) return 'firefox-mobile';
    if (isSamsungBrowser) return 'samsung';
    if (isOperaMobile) return 'opera-mobile';
    if (isAndroidChromium) return 'android-chromium';
    if (isDesktopChromium) return 'desktop-chromium';
    if (isMobile) return 'mobile-generic';
    return 'other';
  })();

  const handleClick = async () => {
    if (track === 'native') {
      const result = await promptInstall();
      if (result?.outcome === 'accepted') {
        toast.success('Installing GroovLens…');
      } else if (result?.outcome === 'dismissed') {
        toast('No worries — you can come back anytime.');
      }
      return;
    }
    // For all instruction tracks, toggle inline how-to.
    setExpanded((v) => !v);
  };

  const buttonLabel = (() => {
    if (track === 'native') return 'Install app';
    if (track === 'ios') return expanded ? 'Hide instructions' : 'How to install on iPhone';
    if (track === 'firefox-mobile') return expanded ? 'Hide instructions' : 'How to install on Firefox';
    if (track === 'samsung') return expanded ? 'Hide instructions' : 'How to install on Samsung Internet';
    if (track === 'opera-mobile') return expanded ? 'Hide instructions' : 'How to install on Opera';
    if (track === 'android-chromium')
      return expanded ? 'Hide instructions' : 'How to install on Android';
    if (track === 'desktop-chromium')
      return expanded ? 'Hide instructions' : 'How to install on desktop';
    if (track === 'mobile-generic')
      return expanded ? 'Hide instructions' : 'How to install on this phone';
    return expanded ? 'Hide info' : 'About installing';
  })();

  return (
    <div
      className="rounded-xl border border-border bg-card p-4 space-y-3"
      data-testid="pwa-profile-card"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-[hsl(var(--primary))] grid place-items-center text-[hsl(var(--primary-foreground))]">
          <Smartphone className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Install GroovLens as an app</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Get a home-screen icon, faster cold starts, and a full-screen
            experience without browser chrome.
          </p>
        </div>
      </div>

      {expanded && (
        <div
          className="rounded-lg bg-secondary/40 border border-border/60 p-3 text-xs text-muted-foreground space-y-1.5"
          data-testid="pwa-profile-instructions"
        >
          {track === 'ios' && (
            <>
              <p className="text-foreground font-medium mb-1">On iPhone / iPad</p>
              <p className="flex items-center gap-1.5 flex-wrap">
                1. Tap the <Share className="inline w-3.5 h-3.5" /> Share icon at the bottom of Safari.
              </p>
              <p className="flex items-center gap-1.5 flex-wrap">
                2. Scroll and choose <Plus className="inline w-3.5 h-3.5" /> &ldquo;Add to Home Screen&rdquo;.
              </p>
              <p>3. Confirm the name and tap Add. Done.</p>
            </>
          )}

          {track === 'android-chromium' && (
            <>
              <p className="text-foreground font-medium mb-1">On Android (Chrome / Edge)</p>
              <p className="flex items-center gap-1.5 flex-wrap">
                1. Tap the <MoreVertical className="inline w-3.5 h-3.5" /> menu in the top-right.
              </p>
              <p>2. Choose &ldquo;Install app&rdquo; or &ldquo;Add to Home screen&rdquo;.</p>
              <p>3. Confirm. GroovLens will appear on your home screen.</p>
              <p className="text-foreground/80 mt-1">
                <Info className="inline w-3 h-3 mr-1" />
                You may also see a one-tap install banner here after using the
                app for a moment — Chrome decides when to surface it.
              </p>
            </>
          )}

          {track === 'firefox-mobile' && (
            <>
              <p className="text-foreground font-medium mb-1">On Firefox (mobile)</p>
              <p className="flex items-center gap-1.5 flex-wrap">
                1. Tap the <MoreVertical className="inline w-3.5 h-3.5" /> menu in the top-right of Firefox.
              </p>
              <p>2. Choose <span className="text-foreground">Install</span> (or &ldquo;Add to Home screen&rdquo; on older versions).</p>
              <p className="text-foreground/80 mt-1">
                <Info className="inline w-3 h-3 mr-1" />
                Don&apos;t see the option? Firefox needs to be on a recent version. For the most reliable install, open <span className="font-mono">groovlens.com</span> in <Chrome className="inline w-3 h-3 mx-0.5" /> Chrome (Android) or Safari (iPhone).
              </p>
            </>
          )}

          {track === 'samsung' && (
            <>
              <p className="text-foreground font-medium mb-1">On Samsung Internet</p>
              <p className="flex items-center gap-1.5 flex-wrap">
                1. Tap the <MoreVertical className="inline w-3.5 h-3.5" /> menu (three lines).
              </p>
              <p>2. Choose <span className="text-foreground">Add page to</span> &rarr; <span className="text-foreground">Home screen</span>.</p>
            </>
          )}

          {track === 'opera-mobile' && (
            <>
              <p className="text-foreground font-medium mb-1">On Opera (mobile)</p>
              <p className="flex items-center gap-1.5 flex-wrap">
                1. Tap the <MoreVertical className="inline w-3.5 h-3.5" /> menu.
              </p>
              <p>2. Choose <span className="text-foreground">Add to&hellip;</span> &rarr; <span className="text-foreground">Home screen</span>.</p>
            </>
          )}

          {track === 'mobile-generic' && (
            <>
              <p className="text-foreground font-medium mb-1">On your browser</p>
              <p className="flex items-center gap-1.5 flex-wrap">
                1. Open your browser&apos;s <MoreVertical className="inline w-3.5 h-3.5" /> menu (3 dots / lines).
              </p>
              <p>2. Look for <span className="text-foreground">Install</span>, <span className="text-foreground">Install app</span>, or <span className="text-foreground">Add to Home screen</span>.</p>
              <p className="text-foreground/80 mt-1">
                <Info className="inline w-3 h-3 mr-1" />
                If your browser doesn&apos;t offer install, open <span className="font-mono">groovlens.com</span> in <Chrome className="inline w-3 h-3 mx-0.5" /> Chrome (Android) or Safari (iPhone) — best PWA support.
              </p>
            </>
          )}

          {track === 'desktop-chromium' && (
            <>
              <p className="text-foreground font-medium mb-1">On desktop (Chrome / Edge)</p>
              <p className="flex items-center gap-1.5 flex-wrap">
                1. Look for the <Download className="inline w-3.5 h-3.5" /> install icon at the right edge of the address bar.
              </p>
              <p className="flex items-center gap-1.5 flex-wrap">
                2. Or open the <MoreVertical className="inline w-3.5 h-3.5" /> three-dot menu &rarr; &ldquo;Install GroovLens&hellip;&rdquo;
              </p>
              <p>3. Confirm. The app opens in its own window with a desktop / Dock icon.</p>
              <p className="text-foreground/80 mt-1">
                <Info className="inline w-3 h-3 mr-1" />
                If you don&apos;t see the install icon yet, Chrome may need a
                moment of engagement first. Try refreshing or browsing the app
                briefly and the option will appear.
              </p>
            </>
          )}

          {track === 'other' && (
            <>
              <p className="text-foreground font-medium mb-1">Your browser doesn&apos;t support one-tap install yet</p>
              <p>For the best install experience:</p>
              <p className="flex items-center gap-1.5 flex-wrap">
                <Chrome className="inline w-3.5 h-3.5" /> Use <span className="text-foreground">Chrome or Edge</span> on Android or desktop.
              </p>
              <p className="flex items-center gap-1.5 flex-wrap">
                <Smartphone className="inline w-3.5 h-3.5" /> Or use <span className="text-foreground">Safari on iPhone / iPad</span> and tap Share &rarr; Add to Home Screen.
              </p>
              <p className="text-foreground/80 mt-1">
                <Info className="inline w-3 h-3 mr-1" />
                The site works great in every browser — installing just gives
                you a home-screen icon and a standalone window.
              </p>
            </>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Button
          variant="default"
          size="sm"
          onClick={handleClick}
          className="rounded-lg"
          data-testid="pwa-profile-install-button"
        >
          <Download className="w-4 h-4 mr-1.5" />
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
}
